import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "lucide-react";
import { RepSelectFields } from "./RepSelectFields";
import { getManufacturerRules } from "../utils/manufacturerRules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dropdown options from Design Order Form
const CAN_SIZES = ["8oz Sleek", "12oz Sleek", "12oz Standard", "16oz", "19.2oz", "24oz"];
const NECK_ENDS = ["200", "202", "206", "209"];
const DESIGN_TYPES = ["New Design", "Revision", "Reprint", "Color Match"];
const BEVERAGE_TYPES = ["Beer", "Hard Seltzer", "Energy Drink", "Soda", "Water", "Juice", "Wine", "RTD Cocktail", "Other"];
const NUM_COLORS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const APPLICATION_TYPES = ["Offset Printing", "Digital Printing", "Shrink Sleeve", "Pressure Sensitive Label", "Direct Print"];
const OVERVARNISH_TYPES = ["Gloss", "Matte", "Satin", "Soft Touch"];
const OVERVARNISH_CONTENTS = ["Full Coverage", "Spot Varnish", "None"];
const INSIDE_VARNISH_CONTENTS = ["Standard", "BPA-NI", "Epoxy", "None"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export const ProjectModal = ({ customers, project, onClose, onSuccess }) => {
  const [manufacturers, setManufacturers] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [form, setForm] = useState({
    name: project?.name || "",
    customer_id: project?.customer_id || "",
    description: project?.description || "",
    project_type: project?.project_type || "external",
    priority: project?.priority || "medium",
    status: project?.status || "new_project",
    manufacturer_id: project?.manufacturer_id || "",
    deadline: project?.deadline ? project.deadline.split('T')[0] : "",
    isr: project?.isr || "",
    osr: project?.osr || "",
    // Design Order Form fields
    copacker: project?.copacker || "",
    brand: project?.brand || "",
    can_size: project?.can_size || "",
    neck_end: project?.neck_end || "",
    design_type: project?.design_type || "",
    beverage_type: project?.beverage_type || "",
    number_of_colors: project?.number_of_colors || "",
    application_type: project?.application_type || "",
    barcode: project?.barcode || "",
    gamer_po: project?.gamer_po || "",
    previous_mfg_design_code: project?.previous_mfg_design_code || "",
    customer_ref_type: project?.customer_ref_type || "new",
    // Varnish fields
    overvarnish_type: project?.overvarnish_type || "",
    overvarnish_content: project?.overvarnish_content || "",
    inside_varnish_content: project?.inside_varnish_content || "",
    // Additional info
    pilot_requested: project?.pilot_requested || false,
    pilot_type: project?.pilot_type || "",
    reference_collateral_sent: project?.reference_collateral_sent || false,
    requires_pasteurization: project?.requires_pasteurization || false,
    pasteurization_temp: project?.pasteurization_temp || "180F",
    requires_specialty_inks: project?.requires_specialty_inks || false,
    sub_status: project?.sub_status || "",
    // Art contact info
    art_contact_name: project?.art_contact_name || "",
    art_contact_phone: project?.art_contact_phone || "",
    art_contact_address: project?.art_contact_address || "",
    art_contact_city: project?.art_contact_city || "",
    art_contact_state: project?.art_contact_state || "",
    art_contact_zip: project?.art_contact_zip || "",
    comments: project?.comments || "",
    // Production / Printed Can Tracker fields
    sku_count: project?.sku_count || "",
    po_number: project?.po_number || "",
    customer_po: project?.customer_po || "",
    proof_date: project?.proof_date ? project.proof_date.split('T')[0] : "",
    pilot_date: project?.pilot_date ? project.pilot_date.split('T')[0] : "",
    lid_type: project?.lid_type || "",
    lid_color: project?.lid_color || "",
    body_height: project?.body_height || "",
    production_notes: project?.production_notes || "",
    tab_color: project?.tab_color || "",
    quantity: project?.quantity || "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchManufacturers();
  }, []);

  const fetchManufacturers = async () => {
    try {
      const res = await axios.get(`${API}/manufacturers`);
      setManufacturers(res.data);
    } catch (err) {
      console.error("Failed to fetch manufacturers");
    }
  };

  // Manufacturer-bound rules
  const selectedMfgName = useMemo(() => {
    const mfg = manufacturers.find((m) => m.id === form.manufacturer_id);
    return mfg ? mfg.name : "";
  }, [form.manufacturer_id, manufacturers]);

  const mfgRules = useMemo(() => getManufacturerRules(selectedMfgName), [selectedMfgName]);

  useEffect(() => {
    if (!selectedMfgName) return;
    setForm((prev) => {
      const updates = { ...prev };
      Object.entries(mfgRules.autoSet).forEach(([k, v]) => { updates[k] = v; });
      mfgRules.hidden.forEach((k) => { updates[k] = ""; });
      return updates;
    });
  }, [selectedMfgName, mfgRules]);

  const isFieldHidden = (name) => mfgRules.hidden.includes(name);
  const isFieldLocked = (name) => mfgRules.locked.includes(name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...form,
        manufacturer_id: form.manufacturer_id || null,
        deadline: form.deadline || null,
        // Clean up empty strings to null
        copacker: form.copacker || null,
        brand: form.brand || null,
        can_size: form.can_size || null,
        neck_end: form.neck_end || null,
        design_type: form.design_type || null,
        beverage_type: form.beverage_type || null,
        number_of_colors: form.number_of_colors || null,
        application_type: form.application_type || null,
        barcode: form.barcode || null,
        gamer_po: form.gamer_po || null,
        previous_mfg_design_code: form.previous_mfg_design_code || null,
        customer_ref_type: form.customer_ref_type || null,
        overvarnish_type: form.overvarnish_type || null,
        overvarnish_content: form.overvarnish_content || null,
        inside_varnish_content: form.inside_varnish_content || null,
        pasteurization_temp: form.requires_pasteurization ? form.pasteurization_temp : null,
        art_contact_name: form.art_contact_name || null,
        art_contact_phone: form.art_contact_phone || null,
        art_contact_address: form.art_contact_address || null,
        art_contact_city: form.art_contact_city || null,
        art_contact_state: form.art_contact_state || null,
        art_contact_zip: form.art_contact_zip || null,
        comments: form.comments || null,
        // Production fields
        sku_count: form.sku_count || null,
        po_number: form.po_number || null,
        customer_po: form.customer_po || null,
        proof_date: form.proof_date || null,
        pilot_date: form.pilot_date || null,
        lid_type: form.lid_type || null,
        lid_color: form.lid_color || null,
        body_height: form.body_height || null,
        production_notes: form.production_notes || null,
        tab_color: form.tab_color || null,
        quantity: form.quantity || null,
        // Additional
        pilot_type: form.pilot_requested ? (form.pilot_type || null) : null,
        sub_status: form.sub_status || null,
        isr: form.isr || null,
        osr: form.osr || null,
      };
      
      if (project) {
        await axios.put(`${API}/projects/${project.id}`, submitData);
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/projects`, submitData);
        toast.success("Project created");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "general", label: "General" },
    { id: "design", label: "Design Info" },
    { id: "production", label: "Production" },
    { id: "varnish", label: "Varnish" },
    { id: "additional", label: "Additional" },
    { id: "contacts", label: "Art Contact" },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-h-[90vh] w-full max-w-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border sticky top-0 bg-[#18181B] z-10">
          <h2 className="text-xl font-bold">{project ? "Edit Project" : "New Project"}</h2>
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* General Tab */}
          {activeTab === "general" && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Project Name *</label>
                <input
                  data-testid="project-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Customer *</label>
                <select
                  data-testid="project-customer-select"
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  required
                >
                  <option value="">Select customer...</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Manufacturer</label>
                <select
                  data-testid="project-manufacturer-select"
                  value={form.manufacturer_id}
                  onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">No manufacturer</option>
                  {manufacturers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">The packaging manufacturer/vendor (Ball, Canpack, etc.)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  data-testid="project-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    data-testid="project-status-select"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="new_project">New Project</option>
                    <option value="graphic_team_review">Graphic Team Review</option>
                    <option value="artwork_submitted">Artwork Submitted</option>
                    <option value="pdf_proof_phase">PDF Proof Phase</option>
                    <option value="swatch_drawdown_phase">Swatch Drawdown Phase</option>
                    <option value="pilot_proof_phase">Pilot Proof Phase</option>
                    <option value="graphics_completed">Graphics Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    data-testid="project-priority-select"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select
                    data-testid="project-type-select"
                    value={form.project_type}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="external">External</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Calendar size={16} />
                    Deadline
                  </label>
                  <input
                    data-testid="project-deadline-input"
                    type="date"
                    value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <RepSelectFields
                isr={form.isr}
                osr={form.osr}
                onChange={(field, value) => setForm({ ...form, [field]: value })}
              />
            </>
          )}

          {/* Design Info Tab */}
          {activeTab === "design" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Copacker</label>
                  <input
                    type="text"
                    value={form.copacker}
                    onChange={(e) => setForm({ ...form, copacker: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter copacker name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Brand</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter brand name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Can Size</label>
                  <select
                    value={form.can_size}
                    onChange={(e) => setForm({ ...form, can_size: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select can size...</option>
                    {CAN_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Neck End</label>
                  <select
                    value={form.neck_end}
                    onChange={(e) => setForm({ ...form, neck_end: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select neck end...</option>
                    {NECK_ENDS.map((end) => (
                      <option key={end} value={end}>{end}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Design Type</label>
                  <select
                    value={form.design_type}
                    onChange={(e) => setForm({ ...form, design_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select design type...</option>
                    {DESIGN_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Beverage Type</label>
                  <select
                    value={form.beverage_type}
                    onChange={(e) => setForm({ ...form, beverage_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select beverage type...</option>
                    {BEVERAGE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {!isFieldHidden("number_of_colors") && (
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Colors</label>
                  <select
                    value={form.number_of_colors}
                    onChange={(e) => setForm({ ...form, number_of_colors: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {NUM_COLORS.map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Application Type
                    {isFieldLocked("application_type") && <span className="text-blue-400 text-xs ml-1">(auto)</span>}
                  </label>
                  <select
                    value={form.application_type}
                    onChange={(e) => setForm({ ...form, application_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                    disabled={isFieldLocked("application_type")}
                    style={isFieldLocked("application_type") ? { opacity: 0.7, cursor: "not-allowed" } : {}}
                  >
                    <option value="">Select application type...</option>
                    {APPLICATION_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Barcode</label>
                  <input
                    type="text"
                    value={form.barcode}
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter barcode"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Gamer P/O</label>
                  <input
                    type="text"
                    value={form.gamer_po}
                    onChange={(e) => setForm({ ...form, gamer_po: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter PO number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Previous MFG Design Code</label>
                  <input
                    type="text"
                    value={form.previous_mfg_design_code}
                    onChange={(e) => setForm({ ...form, previous_mfg_design_code: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Index/Code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Ref No Type</label>
                  <select
                    value={form.customer_ref_type}
                    onChange={(e) => setForm({ ...form, customer_ref_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="new">New</option>
                    <option value="previous">Previous</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Production Tab */}
          {activeTab === "production" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">SKU Count</label>
                  <input type="text" value={form.sku_count} onChange={(e) => setForm({ ...form, sku_count: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="Number of SKUs" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Quantity</label>
                  <input type="text" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="Order quantity" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PO Number</label>
                  <input type="text" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="PO#" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Customer PO</label>
                  <input type="text" value={form.customer_po} onChange={(e) => setForm({ ...form, customer_po: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="Customer PO#" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Lid Type</label>
                  <select value={form.lid_type} onChange={(e) => setForm({ ...form, lid_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg">
                    <option value="">Select...</option>
                    <option value="SOT (Stay-On-Tab)">SOT (Stay-On-Tab)</option>
                    <option value="LOE (Large Opening End)">LOE (Large Opening End)</option>
                    <option value="Full Aperture">Full Aperture</option>
                    <option value="CDL">CDL</option>
                    <option value="Standard">Standard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Lid Color</label>
                  <input type="text" value={form.lid_color} onChange={(e) => setForm({ ...form, lid_color: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="Lid color" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tab Color</label>
                  <input type="text" value={form.tab_color} onChange={(e) => setForm({ ...form, tab_color: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="Tab color" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Body Height</label>
                <input type="text" value={form.body_height} onChange={(e) => setForm({ ...form, body_height: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" placeholder="e.g., 4.812 in" />
              </div>

              <h4 className="text-sm font-semibold text-muted-foreground pt-2">KEY DATES</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Proof Date</label>
                  <input type="date" value={form.proof_date} onChange={(e) => setForm({ ...form, proof_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Pilot Date</label>
                  <input type="date" value={form.pilot_date} onChange={(e) => setForm({ ...form, pilot_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Production Notes</label>
                <textarea value={form.production_notes} onChange={(e) => setForm({ ...form, production_notes: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring" rows="3" placeholder="Additional production notes..." />
              </div>
            </>
          )}

          {/* Varnish Tab */}
          {activeTab === "varnish" && (
            <>
              {isFieldHidden("overvarnish_type") ? (
                <div className="p-4 bg-secondary/30 rounded-lg text-muted-foreground text-sm" style={{ borderLeft: "3px solid #3b82f6" }}>
                  Varnish fields are not applicable for <strong className="text-foreground">{selectedMfgName}</strong>
                </div>
              ) : (
              <>
              <div className="p-4 bg-secondary/30 rounded-lg mb-4">
                <h3 className="font-medium mb-3">External Varnish</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Overvarnish Type</label>
                    <select
                      value={form.overvarnish_type}
                      onChange={(e) => setForm({ ...form, overvarnish_type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                    >
                      <option value="">Select...</option>
                      {OVERVARNISH_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Overvarnish Content</label>
                    <select
                      value={form.overvarnish_content}
                      onChange={(e) => setForm({ ...form, overvarnish_content: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                    >
                      <option value="">Select...</option>
                      {OVERVARNISH_CONTENTS.map((content) => (
                        <option key={content} value={content}>{content}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-secondary/30 rounded-lg">
                <h3 className="font-medium mb-3">Internal Varnish</h3>
                <div>
                  <label className="block text-sm font-medium mb-2">Inside Varnish Content</label>
                  <select
                    value={form.inside_varnish_content}
                    onChange={(e) => setForm({ ...form, inside_varnish_content: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {INSIDE_VARNISH_CONTENTS.map((content) => (
                      <option key={content} value={content}>{content}</option>
                    ))}
                  </select>
                </div>
              </div>
              </>
              )}
            </>
          )}

          {/* Additional Info Tab */}
          {activeTab === "additional" && (
            <>
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.pilot_requested}
                    onChange={(e) => setForm({ ...form, pilot_requested: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <div>
                    <span className="font-medium">Pilot Requested</span>
                    <p className="text-xs text-muted-foreground">Customer would like to have a pilot made</p>
                  </div>
                </label>

                {form.pilot_requested && (
                  <div className="ml-8 p-3 bg-secondary/20 rounded-lg">
                    <label className="block text-sm font-medium mb-2">Pilot Type</label>
                    <select
                      value={form.pilot_type}
                      onChange={(e) => setForm({ ...form, pilot_type: e.target.value })}
                      className="w-48 px-4 py-2.5 bg-background border border-border rounded-lg"
                    >
                      <option value="">Select...</option>
                      <option value="Offset">Offset</option>
                      <option value="Digital">Digital</option>
                    </select>
                  </div>
                )}

                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.reference_collateral_sent}
                    onChange={(e) => setForm({ ...form, reference_collateral_sent: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <div>
                    <span className="font-medium">Reference Collateral Sent</span>
                    <p className="text-xs text-muted-foreground">Labels or master cans sent for color matching</p>
                  </div>
                </label>

                <div className="p-3 bg-secondary/30 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requires_pasteurization}
                      onChange={(e) => setForm({ ...form, requires_pasteurization: e.target.checked })}
                      className="w-5 h-5 rounded border-border"
                    />
                    <span className="font-medium">Requires Pasteurization</span>
                  </label>
                  {form.requires_pasteurization && (
                    <div className="mt-3 ml-8">
                      <label className="block text-sm font-medium mb-2">Temperature</label>
                      <input
                        type="text"
                        value={form.pasteurization_temp}
                        onChange={(e) => setForm({ ...form, pasteurization_temp: e.target.value })}
                        className="w-32 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder="180F"
                      />
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.requires_specialty_inks}
                    onChange={(e) => setForm({ ...form, requires_specialty_inks: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <span className="font-medium">Requires Specialty Inks/Coatings</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Comments / Remarks</label>
                <textarea
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  rows={4}
                  placeholder="Any additional notes or instructions..."
                />
              </div>
            </>
          )}

          {/* Art Contacts Tab */}
          {activeTab === "contacts" && (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Person responsible for approving art related items
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={form.art_contact_name}
                    onChange={(e) => setForm({ ...form, art_contact_name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={form.art_contact_phone}
                    onChange={(e) => setForm({ ...form, art_contact_phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <input
                  type="text"
                  value={form.art_contact_address}
                  onChange={(e) => setForm({ ...form, art_contact_address: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={form.art_contact_city}
                    onChange={(e) => setForm({ ...form, art_contact_city: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <select
                    value={form.art_contact_state}
                    onChange={(e) => setForm({ ...form, art_contact_state: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">ZIP</label>
                  <input
                    type="text"
                    value={form.art_contact_zip}
                    onChange={(e) => setForm({ ...form, art_contact_zip: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="ZIP code"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4 border-t border-border mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="project-submit-btn"
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Saving..." : project ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
